/**
 * 답례품 BEST 상품 규칙 (스토어 판매 화면 ↔ 관리자 편집 화면 공용).
 *
 * DB 변경 없이 원상품(giftItemInfo)의 기존 `tags` 배열에 `베스트` 태그가 있으면 BEST로 본다.
 * - BEST는 **하위분류 단위**다(전체 BEST를 맨 위에 따로 모으지 않는다).
 *   판매 화면의 각 하위분류 구역 안에서 BEST가 맨 앞에 오고(최대 GIFT_BEST_MAX개 = 2열×3줄 한 화면,
 *   가격 낮은 순 — 예산대별로 고르기 쉽게), 나머지는 기존 표시 순서대로 뒤에 이어진다.
 *   하위분류가 없는 카테고리는 카테고리 전체가 한 단위다.
 * - 태그가 원상품에 달려 있어 **전 상조 공통**이다(상조별로 다르게 둘 수 없음).
 *   단, 그 상조가 팔지 않는 상품은 어차피 목록에 없으니 상조마다 보이는 BEST 개수는 다를 수 있다.
 */

export const GIFT_BEST_TAG = '베스트';
export const GIFT_BEST_MAX = 6;

// 운영자가 손으로 입력한 변형도 같은 뜻으로 받는다
const BEST_ALIASES = ['베스트', 'best', '베스트상품', 'best상품'];

export const isGiftBestTag = (tag: string): boolean =>
  BEST_ALIASES.includes(tag.trim().replace(/^#/, '').replace(/\s+/g, '').toLowerCase());

export const hasGiftBestTag = (tags?: string[] | null): boolean =>
  !!tags?.some(isGiftBestTag);

/** 고객 카드의 일반 태그 줄에서 BEST 태그는 뺀다 (배지로 따로 보여주므로 중복 방지) */
export const withoutGiftBestTag = (tags?: string[] | null): string[] =>
  (tags ?? []).filter((tag) => !isGiftBestTag(tag));

/** BEST 지정/해제한 새 태그 배열. 지정 시엔 표준 철자(GIFT_BEST_TAG) 하나만 남긴다 */
/**
 * 한 분류(하위분류 구역) 안의 상품을 "BEST 먼저"로 재배열한다. 판매 화면·관리자 현황판 공용 규칙.
 * - 입력은 이미 표시 순서대로 정렬돼 있다고 본다.
 * - BEST는 앞에서부터 GIFT_BEST_MAX개까지만 인정하고(넘치면 일반 상품으로 취급) 가격 낮은 순으로 놓는다.
 */
export const arrangeGiftBestFirst = <T>(
  items: T[],
  tagsOf: (item: T) => string[] | null | undefined,
  priceOf: (item: T) => number,
): { best: T[]; rest: T[] } => {
  const tagged = items.filter((item) => hasGiftBestTag(tagsOf(item)));
  const best = tagged.slice(0, GIFT_BEST_MAX);
  const bestSet = new Set(best);
  return {
    best: [...best].sort((a, b) => priceOf(a) - priceOf(b)),
    rest: items.filter((item) => !bestSet.has(item)),
  };
};

export const toggleGiftBestTag = (tags: string[] | null | undefined, on: boolean): string[] => {
  const rest = withoutGiftBestTag(tags);
  return on ? [GIFT_BEST_TAG, ...rest] : rest;
};
